import React, { useState, useEffect, useContext } from "react";
import {
  LoadingOverlay,
  Box,
  Container,
  NativeSelect,
  Alert,
  Select,
  Text,
  TextInput,
  Button,
  Group,
  Stack,
  Center,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useMediaQuery } from "@mantine/hooks";
import { DataContext } from "../../../context/DataContext";
import {
  IconShieldCheck,
  IconShieldCancel,
  IconPhoneCheck,
} from "@tabler/icons-react";
import useStatusCheck from "../../../hooks/useStatusCheck";
import DataCollectionForm from "../../../components/data-collection-form";
import useLocale from "../../../hooks/useLocale";
import {
  getAddressRegion,
  isDayFirstRegion,
  ADDRESS_REGION_UK,
  ADDRESS_REGION_DE,
} from "../../../utils/addressRegion";

export default function AgeMatch() {
  const { text, data, settings } = useContext(DataContext);
  // Journey-level `text.general.*` overrides win; the agematch locale set
  // (44 languages, English fallback) fills in everything the journey
  // doesn't set.
  const locale = useLocale("agematch");
  const currentPath = "agematch/process";
  const [formConfig, setFormConfig] = useState(null);
  const [reattempts, setReattempts] = useState(1);

  useEffect(() => {
    const initialFields = [
      {
        name: "fn",
        label: text?.general?.firstName || locale.form.firstName,
        initialValue: data?.fn || "",
        group: "name",
        autoComplete: "given-name",
        enterKeyHint: "next",
        validation: (value) =>
          value.length < 2
            ? text?.general?.firstNameError || locale.form.firstNameError
            : null,
      },
      {
        name: "ln",
        label: text?.general?.lastName || locale.form.lastName,
        initialValue: data?.ln || "",
        group: "name",
        autoComplete: "family-name",
        enterKeyHint: "next",
        validation: (value) =>
          value.length < 2
            ? text?.general?.lastNameError || locale.form.lastNameError
            : null,
      },
    ];

    let additionalFields = [];

    const matchedRoute = settings?.journey?.route?.find(
      (route) => route.path === currentPath,
    );

    if (matchedRoute) {
      setReattempts(matchedRoute.reattempts || 1);
    }

    const inputs = (matchedRoute?.inputs || []).map((input) =>
      typeof input === "string" ? input.toLowerCase() : input,
    );
    const addressRegion = getAddressRegion(settings, currentPath);
    const isUKAddressSetting = addressRegion === ADDRESS_REGION_UK;
    const isDeAddressSetting = addressRegion === ADDRESS_REGION_DE;
    const needsAddress =
      isUKAddressSetting || isDeAddressSetting || inputs.includes("address");
    const needsDob = inputs.includes("dob");

    if (needsAddress) {
      additionalFields.push({
        name: "address",
        label: text?.general?.address || locale.address.label,
        type: "address",
        // Address has its own dedicated step in AgeMatch, so stack the
        // City / State / Zip fields vertically to avoid squishing the Zip.
        stacked: true,
        enterKeyHint: "next",
        validation: (value) => {
          let addrError = locale.form.addressRequired;

          if (isDeAddressSetting) {
            // German: street + house number, 5-digit PLZ, city. Bundesland
            // is optional and travels in `state` ("" when left blank) so
            // the payload keeps the addr/city/state/zip shape the
            // verification consumer maps onto AgeMatch.
            if (
              value.addr &&
              value.city &&
              /^\d{5}$/.test((value.zip || "").trim())
            ) {
              addrError = null;
            }
          } else if (isUKAddressSetting) {
            const ukPostcodePattern =
              /^([A-Z]{1,2}\d[A-Z\d]? ?\d[A-Z]{2})$/i;
            if (
              value.addr &&
              value.city &&
              value.state &&
              ukPostcodePattern.test(value.zip.trim())
            ) {
              addrError = null;
            }
          } else if (
            value.addr &&
            value.city &&
            value.state &&
            value.zip.trim().length === 5
          ) {
            // The address is valid for US format.
            addrError = null;
          }

          return addrError;
        },
      });
    }

    if (needsDob) {
      additionalFields.push({
        name: "dob",
        label: text?.general?.DateOfBirth || locale.form.dob,
        type: "date",
        // UK and DE users type their date day-first; the wire format stays
        // YYYYMMDD (DateOfBirthSelect reorders before onDobChange).
        dayFirst: isDayFirstRegion(addressRegion),
        placeholder: isDayFirstRegion(addressRegion)
          ? locale.form.dobPlaceholderDayFirst
          : locale.form.dobPlaceholderMonthFirst,
        validation: (value) => {
          const dobPattern =
            /^(19|20)\d{2}(0[1-9]|1[0-2])(0[1-9]|[12][0-9]|3[01])$/;
          const isDobFormatCorrect = dobPattern.test(value);

          let dobError =
            text?.general?.DateOfBirthError || locale.form.dobError;

          if (isDobFormatCorrect) {
            const year = parseInt(value.substring(0, 4), 10);
            const month = parseInt(value.substring(4, 6), 10) - 1; // JS months are 0-indexed
            const day = parseInt(value.substring(6, 8), 10);

            const date = new Date(year, month, day);

            if (
              date.getFullYear() === year &&
              date.getMonth() === month &&
              date.getDate() === day
            ) {
              dobError = null;
            }
          }
          return dobError;
        },
      });
    }

    setFormConfig({ fields: [...initialFields, ...additionalFields] });
  }, [data, currentPath, text, settings, locale]);

  if (!formConfig) {
    return <LoadingOverlay visible />;
  }

  return (
    <DataCollectionForm
      config={formConfig}
      title={locale.header.title}
      subtitle={locale.header.subtitle}
      buttonText={locale.button.verify}
      icon={
        <IconShieldCheck
          size={40}
          stroke={1.5}
          //color={data.styles?.primaryColor ? data.styles.primaryColor : "teal"}
        />
      }
      componentKey={currentPath}
      endpoint="/v3/stable/api/frontend/agematch"
      reAttempts={reattempts}
    />
  );
}
